import { Box, Icon, Typography } from "@outschool/backpack";
import { faPlayCircle } from "@outschool/icons";
import { BoxButton } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useIsMounted } from "@outschool/ui-utils";
import React from "react";

import MuxVideoPlayer from "./MuxVideoPlayer";

export type TabConfig = {
  title: string;
  description?: string;
  videoPlaybackID: string;
  thumbnailUrl?: string;
};

type TabbedVideoPlayerProps = {
  tabs: TabConfig[];
  trackTouch?: () => void;
};

const TabbedVideoPlayer = (
  { tabs = [], trackTouch = () => {} }: TabbedVideoPlayerProps,
  ref: React.Ref<HTMLDivElement>,
) => {
  const isMounted = useIsMounted();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = React.useState(0);

  if (!isMounted) {
    return <></>;
  }

  return isMobile ? (
    <TabbedVideoPlayerMobile
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      trackTouch={trackTouch}
      tabs={tabs}
      ref={ref}
    />
  ) : (
    <TabbedVideoPlayerDesktop
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      trackTouch={trackTouch}
      tabs={tabs}
      ref={ref}
    />
  );
};

type TabbedVideoPlayerDesktopProps = {
  activeTab: number;
  setActiveTab: (t: number) => void;
  trackTouch: () => void;
  tabs: TabConfig[];
};

const TabbedVideoPlayerDesktop = React.forwardRef(
  (
    {
      activeTab,
      setActiveTab,
      trackTouch,
      tabs,
    }: TabbedVideoPlayerDesktopProps,
    ref,
  ) => {
    const { videoPlaybackID, thumbnailUrl } = tabs[activeTab];

    return (
      <Box
        flex
        ref={ref}
        sx={{
          gap: "3em",
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: "column",
            gap: "0.5em",
            flexBasis: 430,
            flexShrink: 0,
          }}
        >
          {tabs.map(({ title, description }, idx) => {
            const isActive = activeTab === idx;
            return (
              <Box key={idx}>
                <BoxButton
                  onClick={() => {
                    setActiveTab(idx);
                  }}
                  sx={{
                    textAlign: "left",
                    cursor: "pointer",
                    padding: "medium",
                    borderTopLeftRadius: "xlarge",
                    borderTopRightRadius: "xlarge",
                    borderBottomLeftRadius: isActive ? 0 : "xlarge",
                    borderBottomRightRadius: isActive ? 0 : "xlarge",
                    backgroundColor: isActive ? "sunshine500" : "gray100",
                  }}
                >
                  <Typography variant="h5">{title}</Typography>
                </BoxButton>
                {description && activeTab === idx && (
                  <Box
                    sx={{
                      backgroundColor: "warning.100",
                      padding: "1em",
                      borderBottomLeftRadius: "1em",
                      borderBottomRightRadius: "1em",
                      zIndex: 1,
                      textAlign: "left",
                    }}
                  >
                    {description}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            borderRadius: "1em",
            overflow: "hidden",
          }}
        >
          <MuxVideoPlayer
            playButtonType="white-center"
            videoPlaybackID={videoPlaybackID}
            onClick={trackTouch}
            thumbnailUrl={thumbnailUrl ?? undefined}
            // When the thumbnail is loading, the container has no height.
            // This makes the play button float in space at the top of the render area.
            // Hide overflow so this button is not visible during thumbnail load.
            sx={{ overflow: "hidden" }}
          />
        </Box>
      </Box>
    );
  },
);

type TabbedVideoPlayerMobileProps = {
  activeTab: number;
  setActiveTab: (t: number) => void;
  trackTouch: () => void;
  tabs: TabConfig[];
};

const TabbedVideoPlayerMobile = React.forwardRef(
  (
    { activeTab, setActiveTab, trackTouch, tabs }: TabbedVideoPlayerMobileProps,
    ref,
  ) => (
    <Box ref={ref}>
      {tabs.map(({ title, videoPlaybackID, thumbnailUrl }, idx) => {
        const isActive = activeTab === idx;
        return (
          <BoxButton
            key={idx}
            onClick={() => {
              setActiveTab(idx);
            }}
            sx={{
              flexWrap: "nowrap",
              width: "100%",
              backgroundColor: isActive ? "sunshine500" : "gray100",
              borderRadius: "xlarge",
              cursor: "pointer",
              zIndex: 2,
              marginBottom: "small",
              overflow: "hidden",
            }}
          >
            <Box
              flex
              sx={{
                padding: "1em",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {!isActive && (
                <Icon
                  sx={{
                    position: "absolute",
                    left: "1.2em",
                    fontSize: "1.333em",
                  }}
                  fixedWidth
                  icon={faPlayCircle}
                />
              )}
              <Typography variant="h5">{title}</Typography>
            </Box>
            {isActive && (
              <Box
                sx={{
                  maxWidth: 400,
                  height: 225,
                  margin: 10,
                }}
              >
                <MuxVideoPlayer
                  playButtonType="white-center"
                  videoPlaybackID={videoPlaybackID}
                  thumbnailUrl={thumbnailUrl ?? undefined}
                  onClick={trackTouch}
                  sx={{
                    borderBottomLeftRadius: "xlarge",
                    borderBottomRightRadius: "xlarge",
                    // When the thumbnail is loading, the container has no height.
                    // This makes the play button float in space at the top of the render area.
                    // Hide overflow so this button is not visible during thumbnail load.
                    overflow: "hidden",
                  }}
                />
              </Box>
            )}
          </BoxButton>
        );
      })}
    </Box>
  ),
);

export default React.forwardRef(TabbedVideoPlayer);
